/**
 * ConflictRulesPage — Compliance · Conflict Rules (the 40-check menu).
 *
 * The catalog of cross-document conflict checks (brief §3, Tiers A/B/C) that
 * Slot BL's `detectCrossDocConflicts` + Slot TR's `ConflictDetectionService`
 * evaluate at capture/commit. Per check the admin can:
 *   - toggle ACTIVE (Bill's "deactivate undesirable options" surface), and
 *   - re-tier SEVERITY: major → red ALERT (blocks idScan) / minor → officer
 *     "pick" / info → surfaced only.
 * `scope` + `label` are read-only catalog metadata. There is deliberately NO
 * delete — a referenced ruleId must never be orphaned (no-system-breaking-edits);
 * deactivate instead.
 *
 * Contract: GET /v3/config/conflict-rules (Slot TR seeds all 40 as a catalog
 * with proposed default tiers; Bill toggles). PATCH /v3/admin/conflict-rules/{ruleId}.
 * Routes not live in this branch yet → non-fatal "seed pending" banner.
 *
 * 5 admin-quality bars: wired (read + per-row PATCH) · configurable (active +
 * severity per check) · makes sense (grouped by tier, default-drift shown) ·
 * modern control (inline severity select + active toggle, optimistic) ·
 * no-breaking (no delete; deactivate only; scope/label locked).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import {
  PersonModalConfigApi,
  errMessage,
  isRouteNotLive,
  type ConflictRule,
  type ConflictSeverity,
} from '@/services/personModalConfigApi';

const SEVERITY_OPTS: ReadonlyArray<{ value: ConflictSeverity; label: string }> = [
  { value: 'major', label: 'Major — ALERT (blocks)' },
  { value: 'minor', label: 'Minor — officer pick' },
  { value: 'info', label: 'Info — surfaced only' },
];

function severityPill(sev: ConflictSeverity): JSX.Element {
  if (sev === 'major') return <Pill tone="err">MAJOR · alert</Pill>;
  if (sev === 'minor') return <Pill tone="warn">MINOR · pick</Pill>;
  return <Pill tone="info">INFO</Pill>;
}

function tierLabel(tier: string | undefined): string {
  if (!tier) return 'Ungrouped';
  return /^[A-Za-z]$/.test(tier) ? `Tier ${tier.toUpperCase()}` : tier;
}

export function ConflictRulesPage(): JSX.Element {
  const client = useApiClient();
  const api = useMemo(() => new PersonModalConfigApi(client), [client]);

  const [rows, setRows] = useState<ReadonlyArray<ConflictRule> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [routePending, setRoutePending] = useState(false);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      setRows(null);
      setLoadError(null);
      setRoutePending(false);
      try {
        const resp = await api.listConflictRules(ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRows(resp.data);
        } else {
          setRows([]);
          setLoadError(resp.error ?? 'Failed to load conflict rules.');
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        if (isRouteNotLive(e)) {
          setRows([]);
          setRoutePending(true);
        } else {
          setRows([]);
          setLoadError(errMessage(e));
        }
      }
    })();
    return () => ctrl.abort();
  }, [api, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const byTier = useMemo(() => {
    if (!rows) return [];
    const map = new Map<string, ConflictRule[]>();
    for (const r of rows) {
      const key = r.tier ?? '';
      let list = map.get(key);
      if (!list) {
        list = [];
        map.set(key, list);
      }
      list.push(r);
    }
    for (const list of map.values()) list.sort((a, b) => a.label.localeCompare(b.label));
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const counts = useMemo(() => {
    const c = { major: 0, minor: 0, info: 0, inactive: 0 };
    for (const r of rows ?? []) {
      if (!r.isActive) c.inactive++;
      else c[r.severity]++;
    }
    return c;
  }, [rows]);

  // Optimistic PATCH — update locally, revert + report on failure.
  const applyPatch = useCallback(
    async (ruleId: string, patch: { severity?: ConflictSeverity; isActive?: boolean }) => {
      const prev = rows;
      if (!prev) return;
      setActionError(null);
      setRows(prev.map((r) => (r.ruleId === ruleId ? { ...r, ...patch } : r)));
      setPending((s) => new Set(s).add(ruleId));
      try {
        const resp = await api.patchConflictRule(ruleId, patch);
        if (!resp.success) throw new Error(resp.error ?? 'patch returned success=false');
        const what = patch.severity ? `severity → ${patch.severity}` : `${patch.isActive ? 'activated' : 'deactivated'}`;
        setToast({ tone: 'info', text: `${ruleId}: ${what}.` });
      } catch (e) {
        setRows(prev); // revert
        setActionError(
          isRouteNotLive(e)
            ? 'Write route not live in this branch yet (Slot TR pending). The editor is wired; PATCH works once TR ships /v3/admin/conflict-rules/{id}.'
            : errMessage(e),
        );
      } finally {
        setPending((s) => {
          const next = new Set(s);
          next.delete(ruleId);
          return next;
        });
      }
    },
    [rows, api],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load conflict rules: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading conflict rules…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}
      {actionError && <Banner tone="err">{actionError}</Banner>}

      <Banner tone="info">
        The cross-document conflict catalog. <strong>Major</strong> checks raise a red ALERT that
        blocks ID-scan clearance (possible non-genuine ID); <strong>minor</strong> checks let the
        officer pick which value to keep; <strong>info</strong> checks are surfaced without gating.
        Toggle a check off to stop it firing. Checks can’t be deleted (a referenced rule must never
        be orphaned) — deactivate instead.
      </Banner>

      {routePending && (
        <Banner tone="warn">
          <strong>Seed pending.</strong> The <code>/v3/config/conflict-rules</code> route isn’t live
          in this branch yet (Slot TR seeds all 40 checks with proposed default tiers). This editor
          is wired against the contract and will populate once TR ships.
        </Banner>
      )}

      {rows.length > 0 && (
        <Banner tone="info">
          {rows.length} checks ·{' '}
          <Pill tone="err">MAJOR {counts.major}</Pill> <Pill tone="warn">MINOR {counts.minor}</Pill>{' '}
          <Pill tone="info">INFO {counts.info}</Pill>{' '}
          {counts.inactive > 0 && <Pill tone="muted">inactive {counts.inactive}</Pill>}
        </Banner>
      )}

      {rows.length === 0 && !routePending && (
        <Card title="Conflict Rules">
          <p className="empty">No conflict-rule rows returned.</p>
        </Card>
      )}

      {byTier.map(([tier, list]) => (
        <Card key={tier || 'ungrouped'} title={`${tierLabel(tier)} — ${list.length}`}>
          <div className="data-table__wrap">
            <table className="data-table conflict-table">
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Scope</th>
                  <th>Severity</th>
                  <th>Default</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => {
                  const busy = pending.has(r.ruleId);
                  const drifted = r.severity !== r.defaultSeverity;
                  return (
                    <tr key={r.ruleId} className={r.isActive ? undefined : 'conflict-row--off'}>
                      <td>
                        <div className="cell-strong">{r.label}</div>
                        <div className="cell-mono">{r.ruleId}</div>
                        {r.description && <div className="cell-desc">{r.description}</div>}
                      </td>
                      <td>
                        <code className="cell-mono">{r.scope}</code>
                      </td>
                      <td>
                        <div className="conflict-sev">
                          {severityPill(r.severity)}
                          <select
                            className="form-input conflict-sev__select"
                            value={r.severity}
                            disabled={busy || !r.isActive}
                            onChange={(e) =>
                              void applyPatch(r.ruleId, { severity: e.target.value as ConflictSeverity })
                            }
                            aria-label={`Severity for ${r.label}`}
                          >
                            {SEVERITY_OPTS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td>
                        {drifted ? (
                          <span className="cell-desc" title="Differs from seeded default">
                            default: {r.defaultSeverity}
                          </span>
                        ) : (
                          <span className="cell-dim">—</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`prio-toggle${r.isActive ? ' prio-toggle--on' : ''}`}
                          disabled={busy}
                          aria-label={`${r.label} is ${r.isActive ? 'active' : 'inactive'}; click to ${
                            r.isActive ? 'deactivate' : 'activate'
                          }`}
                          onClick={() => void applyPatch(r.ruleId, { isActive: !r.isActive })}
                        >
                          {busy ? '…' : r.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </>
  );
}
